import { useAuth0 } from '@auth0/auth0-react';
import { Role } from 'consts/auth';
import { isFullAccessRoutePattern } from 'consts/route-access';
import { useHasRole, useIsPLOnly } from 'hooks/use-auth';
import { signInPath } from 'lib/login-method';
import { LoadingPage } from 'pages';
import React from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';

// HACK: PL-only users may only land on the Petro-Logistics Shiptracking page.
// This is a frontend-only redirect — backend APIs are not gated. Move to a
// proper backend allowlist + MCP server guard ASAP.
const PL_ONLY_ALLOWED_PATH = '/petrologistics-shiptracking';

interface PrivateRouteProps {
  role?: Role;
}

export function PrivateRoute ({
  component: Component,
  children,
  render,
  role,
  path,
  ...args
}: PrivateRouteProps & RouteProps) {
  const { isAuthenticated, isLoading } = useAuth0();
  // Which routes need a subscription lives in consts/route-access, shared with
  // the sidebar so a nav entry can't disagree with the route it points at.
  // An explicit `role` prop still wins, for one-off gates outside that list.
  const requiredRole =
    role ?? (isFullAccessRoutePattern(path) ? Role.FullAccess : Role.BasicAccess);
  const userHasRole = useHasRole(requiredRole);
  const hasFullAccess = useHasRole(Role.FullAccess);
  const isPLOnly = useIsPLOnly();
  const userCanAccess = userHasRole || hasFullAccess;
  // HACK: PL-only users get bounced off any route that isn't the PL page.
  const blockedForPLOnly = isPLOnly && path !== PL_ONLY_ALLOWED_PATH;

  if (children) {
    throw new Error('children not supported for private routes');
  }

  if (render) {
    throw new Error('render not supported for private routes');
  }

  if (!Component) {
    throw new Error('component to render must be defined');
  }

  // Memoize the protected wrapper ONCE
  const ProtectedComponent = React.useMemo(() => {
    const ProtectedRoute = (routeProps: RouteComponentProps) => {
      const { location } = routeProps;
      if (isLoading) {
        return <LoadingPage message="Initializing" />;
      }
      // Signed-out visitors go to the in-app sign-in page (which offers the
      // code-vs-password choice) rather than straight to Universal Login,
      // carrying the current URL so they land back here afterwards.
      if (!isAuthenticated) {
        return <Redirect to={signInPath(location)} />;
      }
      // HACK: PL-only short-circuit — sends user to the only page they may use.
      if (blockedForPLOnly) {
        return <Redirect to={PL_ONLY_ALLOWED_PATH} />;
      }
      if (userCanAccess) {
        return <Component {...routeProps} />;
      }
      // Authenticated but lacking the route's role (free / expired-trial
      // users on a FullAccess route): send them to the Agent rather than the
      // signup pitch — they already have an account, and chat plus the single
      // market-overview watchlist is the whole free-tier surface.
      return <Redirect to="/chat" />;
    };
    return ProtectedRoute;
  }, [Component, isAuthenticated, isLoading, userCanAccess, blockedForPLOnly]);

  return <Route {...args} path={path} component={ProtectedComponent} />;
}
